import React, { useMemo, useContext } from "react";
import { useMutation, queryCache, QueryKey } from "react-query";
import { AxiosError } from "axios";

import { ApiError } from "fe-shared/src/types/common";
import { AssigneeSelector } from "fe-shared/src/components";
import UserContext from "contexts/userContext";

import { Invoice, InvoiceStatusCode } from "fe-shared-transactions/src/types";

import * as api from "./modules/api";
import { INVOICE_APPROVAL_ROLES, GP_SYNC_ROLES } from "consts";

const assignStatuses = [
  InvoiceStatusCode.InReview,
  InvoiceStatusCode.Submitted,
  InvoiceStatusCode.InApproval,
  InvoiceStatusCode.Approved,
];

const invoiceApprovalRoles = [
  INVOICE_APPROVAL_ROLES.InvoiceApprovalCEO,
  INVOICE_APPROVAL_ROLES.InvoiceApprovalExecutive,
  INVOICE_APPROVAL_ROLES.InvoiceApprovalDirector,
  INVOICE_APPROVAL_ROLES.InvoiceApprovalManager,
  INVOICE_APPROVAL_ROLES.InvoiceApprovalClerk
];

const GPSendingRoles = [
  GP_SYNC_ROLES.FinanceClerk
];

interface Props {
  canAssign?: boolean;
  isPostedToGP: boolean;
  invoice: Invoice;
  assigneeName?: string;
  assigneeId?: string | number;
  invoiceQueryKey: QueryKey;
  activitiesQueryKey: QueryKey;
}

const AssigneeInvoiceSelect = ({
  canAssign = true,
  isPostedToGP,
  invoice,
  assigneeId,
  assigneeName,
  invoiceQueryKey,
  activitiesQueryKey,
}: Props) => {
  const { invoiceId, statusCode } = invoice;
  const { userId: currentUserId } = useContext(UserContext)!;

  const isAssignable = useMemo(
    () => canAssign && assignStatuses.includes(statusCode) && !isPostedToGP,
    [statusCode, canAssign]
  );

  const [changeAssignee] = useMutation<
    any,
    AxiosError<ApiError>,
    api.AssignInvoiceParams
  >(api.assignInvoice, {
    throwOnError: true,
    onSuccess: () => {
      queryCache.invalidateQueries(invoiceQueryKey);

      if (activitiesQueryKey) {
        queryCache.invalidateQueries(activitiesQueryKey);
      }
    },
  });

  const isAssigned = !!assigneeId;

  const isApproved = InvoiceStatusCode.Approved === statusCode;

  const filteringRoles: string[] = isApproved ? GPSendingRoles : invoiceApprovalRoles;

  const onChange = (newAssigneeId: string | number | null) => {
    changeAssignee({
      userId: newAssigneeId,
      invoiceId,
    });
  };

  return (
    <AssigneeSelector
      entityId={invoiceId.toString()}
      isAssignable={isAssignable}
      isUnAssignable={false}
      loadOnlyActive={true}
      isAssigned={isAssigned}
      currentUserId={currentUserId}
      assigneeId={assigneeId}
      assigneeName={assigneeName}
      filteringRoles={filteringRoles}
      onChange={onChange}
    />
  );
};

export default AssigneeInvoiceSelect;
